import { sortArrayBy } from '@domain-group/javascript-utils'

import useAgencySelector from '@hooks/useAgencySelector'
import useCurrentUser from '@hooks/useCurrentUser'
import useProviders from '@hooks/useProviders'
import useSelector from '@hooks/useSelector'
import useServices from '@hooks/useServices'

import { EntitiesState } from '@redux/entities'

type filteredDeadlineGroupsParams = {
  agencyProviderDeadlineGroups: EntitiesState['agencyProviderDeadlineGroups'],
  providerDeadlineGroups: EntitiesState['providerDeadlineGroups'],
  selectedId: number,
}

export const filteredDeadlineGroups = (params: filteredDeadlineGroupsParams) => {
  const { agencyProviderDeadlineGroups, providerDeadlineGroups, selectedId } = params

  return Object.entries(agencyProviderDeadlineGroups).reduce((filtered, [key, agencyGroup]) => {
    if (!selectedId || agencyGroup.agency_id === selectedId){
      const providerDeadlineGroup = providerDeadlineGroups[agencyGroup.provider_deadline_group_id]

      if (providerDeadlineGroup){
        // @ts-expect-error TS7053
        filtered[providerDeadlineGroup.provider_id] = providerDeadlineGroup
      }
    }

    return filtered
  }, {})
}

type MergeDeadlinesParams = {
  defaultDeadlines: { [key: string]: { time: string } },
  filteredAgencyDeadlines?: { [key: string]: { time: string } },
  services: EntitiesState['services'],
}

const mergeAndSortDeadlines = (params: MergeDeadlinesParams) => {
  const { defaultDeadlines, filteredAgencyDeadlines = {}, services } = params
  const mergedDeadlines = { ...defaultDeadlines, ...filteredAgencyDeadlines }

  const array: { title: string, time: string }[] = []

  Object.entries(mergedDeadlines).forEach(([key, { time }]) => {
    const [, serviceId] = key.split('_')
    const service = services[serviceId]

    if (service) array.push({ title: service.title, time })
  })

  return sortArrayBy(array, 'asc', 'title')
}

const useProviderDeadlines = () => {
  const { agencyIdParam, selectedAgencyId } = useAgencySelector()

  const { isAgent, isEbuAdmin } = useCurrentUser()

  const { loading } = useProviders({
    requestOptions: { include_provider_alternate_logo: true },
    performHttpRequests: true,
  })

  useServices({ performHttpRequests: true })

  const entities = useSelector(reduxState => reduxState.entities)
  const {
    agencyProviderDeadlineGroups, providerDeadlineGroups, providers, services,
  } = entities

  const agencyDeadlines = filteredDeadlineGroups({
    agencyProviderDeadlineGroups,
    providerDeadlineGroups,
    // @ts-expect-error TS2322
    selectedId: isAgent || isEbuAdmin ? undefined : selectedAgencyId,
  })

  // @ts-expect-error TS2769
  const hasProviderDeadlines = Object.values(providers).some(provider => !!Object.keys(provider.deadlines).length)

  return {
    agencyIdParam,
    agencyDeadlines,
    callbacks: {
      mergeAndSortDeadlines: (
        defaultDeadlines: { [key: string]: { time: string } },
        filteredAgencyDeadlines?: { [key: string]: { time: string } },
      ) => mergeAndSortDeadlines({ defaultDeadlines, filteredAgencyDeadlines, services }),
    },
    hasProviderDeadlines,
    loading,
    providers,
    services,
  }
}

export type UseProviderDeadlinesPayload = ReturnType<typeof useProviderDeadlines>

export default useProviderDeadlines
